/*
 * Ported from Archive/src/taskpane/taskpane.ts — agreement API + Word document/compare flows.
 */

/* global Word, Office, OfficeExtension */

import { fetchApi, getStoredAccessToken } from "./api/client";
import { isTokenExpired } from "./utils/jwt";

/** Same-origin path in dev so webpack proxies to the backend (see webpack.config.js). */
const AGREEMENT_VERSIONS_PATH_BASE = "/contracts/versions";

const AGREEMENT_INCOMING_PATH_BASE = "/contracts/upload-document";

const AGREEMENT_INCOMING_PATH_CLEAN_BASE = "/contracts/upload-clean-document";

const AGREEMENT_COMPARE_PATH = "/contracts/compare";

const AGREEMENT_AI_ANALYSIS_GET_BASE = "/contracts/ai_analysis";

function agreementAiAnalysisGetPath(agreementVersionId: string): string {
  return `${AGREEMENT_AI_ANALYSIS_GET_BASE}/${encodeURIComponent(agreementVersionId)}`;
}

/** `DELETE /contracts/ai_analysis/delete/{agreement_version_id}` */
function agreementAiAnalysisDeletePath(agreementVersionId: string): string {
  return `${AGREEMENT_AI_ANALYSIS_GET_BASE}/delete/${encodeURIComponent(agreementVersionId)}`;
}

/** `POST /contracts/ai_analysis/{version_id}/jobs` */
function agreementAiAnalysisJobsStartPath(agreementVersionId: string): string {
  return `${AGREEMENT_AI_ANALYSIS_GET_BASE}/${encodeURIComponent(agreementVersionId)}/jobs`;
}

/** `GET /contracts/ai_analysis/{version_id}/jobs/active` */
function agreementAiAnalysisJobsActivePath(agreementVersionId: string): string {
  return `${AGREEMENT_AI_ANALYSIS_GET_BASE}/${encodeURIComponent(agreementVersionId)}/jobs/active`;
}

/** `GET /contracts/ai_analysis/jobs/{job_id}` */
function agreementAiAnalysisJobGetPath(jobId: string): string {
  return `${AGREEMENT_AI_ANALYSIS_GET_BASE}/jobs/${encodeURIComponent(jobId)}`;
}

function agreementIncomingUploadPath(agreementVersionId: string): string {
  return `${AGREEMENT_INCOMING_PATH_BASE}/${encodeURIComponent(agreementVersionId)}`;
}

function agreementIncomingCleanUploadPath(agreementVersionId: string): string {
  return `${AGREEMENT_INCOMING_PATH_CLEAN_BASE}/${encodeURIComponent(agreementVersionId)}`;
}

const AGREEMENT_ADD_IN_AI_ANALYSIS_POST_PATH = "/contracts/add_in/ai_analysis";

const AGREEMENT_CHANGES_PATH_BASE = "/contracts/changes";

const AGREEMENT_BULK_CHANGE_PATH = "/contracts/bulk/change";

function agreementChangesGetPath(agreementVersionId: string): string {
  return `${AGREEMENT_CHANGES_PATH_BASE}/${encodeURIComponent(agreementVersionId)}`;
}

/** POST `/contracts/paragraph/ai_analysis/{agreement_version_id}` */
function agreementParagraphAiAnalysisPostPath(agreementVersionId: string): string {
  return `/contracts/paragraph/ai_analysis/${encodeURIComponent(agreementVersionId)}`;
}

const AGREEMENT_SUMMARY_PATH_BASE = "/contracts/summary";

/** POST body shape unchanged; path includes the workspace current version id. */
function requireAccessToken(): string {
  const t = getStoredAccessToken();
  if (!t || isTokenExpired(t)) {
    throw new Error("Not signed in or session expired. Sign in from the add-in task pane.");
  }
  return t;
}

export type ProgressCallback = (message: string) => void;

export interface AgreementVersion {
  version: string;
  created_at: string;
  created_by: string;
  version_id?: string;
  display_label?: string;
  is_major?: boolean;
  revision_index?: number | null;
}

/** Rich version row from Contract Hub `items[]` (POST /contracts/versions/{id}). */
export interface VersionSummary {
  id: string;
  version: number;
  is_major: boolean;
  revision_index: number | null;
  display_label: string;
  created_at: string;
  created_by?: string | null;
  sent_by?: string | null;
  is_promotable: boolean;
  is_promoted: boolean;
  is_superseded: boolean;
  major_origin_label?: string | null;
  parent_display_label?: string | null;
  promoted_from_label?: string | null;
  clean: boolean;
  signed: boolean;
  sharepoint_web_url?: string | null;
  sharepoint_sharing_url?: string | null;
  /** Leaf filename from storage (used to prefill promote rename). */
  source_doc_filename?: string | null;
  /** Alias of `id` on some API rows */
  version_id?: string;
}

export interface VersionsResponse {
  versions: AgreementVersion[];
  /** Rich list — preferred when present */
  items?: VersionSummary[];
  current_version: string;
  current_version_id?: string | null;
  display_label?: string | null;
}

/** Prefer `items[]`; fall back to legacy `versions[]` as pseudo-majors. */
export function resolveVersionItems(data: VersionsResponse | null | undefined): VersionSummary[] {
  if (!data) {
    return [];
  }
  if (data.items && data.items.length > 0) {
    return data.items.map((item) => ({
      ...item,
      id: item.id || item.version_id || "",
    }));
  }
  return (data.versions ?? []).map((row) => {
    const match = /^(\d+)/.exec(String(row.version).trim());
    const versionNum = match ? Number.parseInt(match[1], 10) : 0;
    const id = row.version_id?.trim() || `legacy-${row.version}`;
    const display =
      row.display_label?.trim() ||
      (match ? `v${versionNum}` : String(row.version).trim() || "v?");
    return {
      id,
      version: versionNum,
      is_major: row.is_major !== false,
      revision_index: row.revision_index ?? null,
      display_label: display,
      created_at: row.created_at,
      created_by: row.created_by,
      is_promotable: false,
      is_promoted: false,
      is_superseded: false,
      clean: false,
      signed: false,
      version_id: row.version_id,
    };
  });
}

/** GET /contracts/summary/{agreement_version_id} */
export type AgreementSummaryResponse = {
  summary: string;
  current_version: number;
  initiatedBy: string;
};

/** GET /contracts/summary/ai_analysis/{agreement_version_id} */
export type AgreementAiAnalysisFromSummaryResponse = {
  aiAnalysis: string | null;
  aiAnalysisActions: { high: number; medium: number; low?: number };
};

interface CompareResponse {
  encoded: string;
}

/** Body paragraph under a heading-based section (internal grouping). */
export type LegalDocumentBodyParagraph = {
  index: number;
  text: string;
};

/** Flat chunk sent to `POST .../add_in/ai_analysis` — only `index` + `text`. */
export type DocumentParagraphChunk = {
  index: number;
  text: string;
};

/** One revision entry from `GET .../changes` (see `mock/changes.json`). */
export type AgreementChangeRevision = {
  type: string;
  text: string;
  author: string | null;
  date: string | null;
};

/** Granular diff segment (same shape as AI analysis `word_changes`). */
export type AnalysisWordChange = {
  type: "equal" | "deletion" | "insertion";
  text: string;
};

/** One paragraph change from `GET .../changes` — also the POST body for paragraph-level AI analysis. */
export type AgreementChangeItem = {
  index: number;
  text: string;
  /** Tracked revisions; when `type` is `equal` | `deletion` | `insertion`, same shape as AI `word_changes` for diff UI. */
  revisions: AgreementChangeRevision[];
};

/** Response shape from `GET /contracts/changes/{agreement_version_id}`. */
export type AgreementChangesResponse = {
  changes: AgreementChangeItem[];
};

/** Section built from Heading styles + following body paragraphs until the next heading. */
export type LegalDocumentSection = {
  heading: string;
  level: number;
  /**
   * Word `document.body.paragraphs.items` index of the paragraph that has the Heading style for this section.
   * `null` for the synthetic "Preamble" block (no heading paragraph in the file).
   */
  headingParagraphIndex: number | null;
  paragraphs: LegalDocumentBodyParagraph[];
};

function headingLevelFromStyle(style: string | undefined | null): number | null {
  if (style == null || style === "") {
    return null;
  }
  const s = String(style);
  if (!s.startsWith("Heading")) {
    return null;
  }
  const n = parseInt(s.replace(/^Heading/i, "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Groups the document body by built-in Heading styles (Heading1…Heading9).
 * Text before the first heading is collected under a synthetic "Preamble" section.
 */
export async function extractLegalDocument(): Promise<LegalDocumentSection[]> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();
    for (const para of paragraphs.items) {
      para.load(["text", "styleBuiltIn"]);
    }
    await context.sync();

    const sections: LegalDocumentSection[] = [];
    let currentSection: LegalDocumentSection | null = null;

    paragraphs.items.forEach((para, index) => {
      const text = (para.text ?? "").trim();
      const style = para.styleBuiltIn;
      const level = headingLevelFromStyle(style as string);

      if (!text) {
        return;
      }

      if (level != null) {
        currentSection = {
          heading: text,
          level,
          headingParagraphIndex: index,
          paragraphs: [],
        };
        sections.push(currentSection);
      } else {
        if (!currentSection) {
          currentSection = {
            heading: "Preamble",
            level: 0,
            headingParagraphIndex: null,
            paragraphs: [],
          };
          sections.push(currentSection);
        }
        currentSection.paragraphs.push({
          index,
          text,
        });
      }
    });

    return sections;
  });
}

/**
 * Turns section grouping into a flat list of `{ index, text }` for the API (document order).
 */
export function flattenDocumentParagraphs(sections: LegalDocumentSection[]): DocumentParagraphChunk[] {
  const out: DocumentParagraphChunk[] = [];
  for (const sec of sections) {
    if (sec.headingParagraphIndex != null) {
      out.push({ index: sec.headingParagraphIndex, text: sec.heading });
    }
    for (const bp of sec.paragraphs) {
      out.push({ index: bp.index, text: bp.text });
    }
  }
  out.sort((a, b) => a.index - b.index);
  return out;
}

function paragraphChunksToSnapshot(chunks: DocumentParagraphChunk[]): Record<number, string> {
  const map: Record<number, string> = {};
  for (const c of chunks) {
    map[c.index] = c.text;
  }
  return map;
}

function parseWordChanges(raw: unknown): AnalysisWordChange[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const out: AnalysisWordChange[] = [];
  for (const item of raw) {
    const o = item as Record<string, unknown>;
    const t = typeof o.type === "string" ? o.type : "";
    const text = typeof o.text === "string" ? o.text : "";
    if (t === "equal" || t === "deletion" || t === "insertion") {
      out.push({ type: t, text });
    }
  }
  return out;
}

export function parseAgreementChangesResponse(data: unknown): AgreementChangesResponse {
  if (data === null || typeof data !== "object") {
    return { changes: [] };
  }
  const ch = (data as { changes?: unknown }).changes;
  if (!Array.isArray(ch)) {
    return { changes: [] };
  }
  const changes: AgreementChangeItem[] = ch.map((raw) => {
    const o = raw as Record<string, unknown>;
    const index = typeof o.index === "number" ? o.index : Number(o.index);
    const text = typeof o.text === "string" ? o.text : "";
    const revisions = Array.isArray(o.revisions) ? (o.revisions as AgreementChangeRevision[]) : [];
    return { index, text, revisions };
  });
  return { changes };
}

/**
 * Maps `/changes` `revisions[]` into segments for {@link DiffRenderer} (same shape as AI analysis `word_changes`).
 * Only includes entries whose `type` is `equal`, `deletion`, or `insertion`.
 */
export function revisionsToDiffSegments(revisions: AgreementChangeRevision[] | undefined): AnalysisWordChange[] {
  if (!revisions?.length) {
    return [];
  }
  const out: AnalysisWordChange[] = [];
  for (const r of revisions) {
    const t = typeof r.type === "string" ? r.type : "";
    if (t === "equal" || t === "deletion" || t === "insertion") {
      out.push({ type: t, text: typeof r.text === "string" ? r.text : "" });
    }
  }
  return out;
}

/** `index` → current paragraph text from the changes API (for Apply / locate). */
export function buildParagraphSnapshotFromAgreementChanges(items: AgreementChangeItem[]): Record<number, string> {
  const map: Record<number, string> = {};
  for (const item of items) {
    if (typeof item.index === "number" && typeof item.text === "string") {
      map[item.index] = item.text;
    }
  }
  return map;
}

/** Build Apply snapshot from saved or computed analysis rows (`text` + `index` per paragraph). */
export function buildParagraphSnapshotFromAnalysisResults(paragraphs: AnalysisParagraphResult[]): Record<number, string> {
  const map: Record<number, string> = {};
  for (const p of paragraphs) {
    const t = p.text?.trim();
    if (typeof p.index === "number" && t) {
      map[p.index] = t;
    }
  }
  return map;
}

const AI_PROGRESS_TEXT_MAX = 100;

/** Trims whitespace and truncates for loading messages (e.g. “Running AI analysis on: …”). */
export function trimProgressText(text: string, maxLen = AI_PROGRESS_TEXT_MAX): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= maxLen) {
    return t;
  }
  return `${t.slice(0, maxLen)}…`;
}

export function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") {
    return true;
  }
  return err instanceof Error && err.name === "AbortError";
}

export type AiChangeReviewStatus = "pending" | "accepted" | "rejected";

/** One row from add-in AI analysis API response (`paragraphs[]`). */
export type AnalysisParagraphResult = {
  /** Server-side UUID for this AI change (used in bulk accept/reject). */
  id?: string;
  index: number;
  compliant: boolean;
  severity: "low" | "medium" | "high";
  suggestion: string;
  /** Server-side review status returned by the API. */
  reviewStatus?: AiChangeReviewStatus;
  /** Source paragraph copy from API (`text`, `original_text`, or legacy `originalText`). */
  text?: string;
  /** Human-readable analysis from the API (used in UI). */
  analysis?: string;
  /** Optional diff payload from API for granular in-paragraph edits. */
  wordChanges?: AnalysisWordChange[];
};

/** Stable key for AI analysis row status (prefer server UUID). */
export function analysisRowKey(row: AnalysisParagraphResult, arrayIndex: number): string {
  return row.id ?? `${row.index}-${arrayIndex}`;
}

export type AddInAnalysisApiResponse = {
  paragraphs: AnalysisParagraphResult[];
};

export function parseAddInAnalysisResponse(data: unknown): AnalysisParagraphResult[] {
  if (data === null || typeof data !== "object") {
    return [];
  }
  const paragraphs = (data as { paragraphs?: unknown; paragraph?: unknown }).paragraphs ?? (data as { paragraph?: unknown }).paragraph;
  if (!Array.isArray(paragraphs)) {
    return [];
  }
  return paragraphs.map((p) => {
    const o = p as Record<string, unknown>;
    const idx = o.index;
    const index = typeof idx === "number" ? idx : Number(idx);
    /** BE may send `text`, `original_text`, or legacy `originalText` (source paragraph, not suggestion). */
    const paragraphText =
      typeof o.text === "string"
        ? o.text
        : typeof o.original_text === "string"
          ? o.original_text
          : typeof o.originalText === "string"
            ? o.originalText
            : "";
    const analysis = o.analysis;
    const wordChanges = parseWordChanges(o.word_changes ?? o.wordChanges);
    const severityRaw = o.severity;
    const severity: "low" | "medium" | "high" =
      severityRaw === "low" || severityRaw === "medium" || severityRaw === "high" ? severityRaw : "medium";
    const id = typeof o.id === "string" ? o.id : undefined;
    const rawStatus = typeof o.review_status === "string" ? o.review_status
      : typeof o.reviewStatus === "string" ? o.reviewStatus as string : undefined;
    const reviewStatus: AiChangeReviewStatus | undefined =
      rawStatus === "accepted" || rawStatus === "rejected" || rawStatus === "pending" ? rawStatus : undefined;
    return {
      id,
      index: Number.isFinite(index) ? index : 0,
      compliant: Boolean(o.compliant),
      severity,
      suggestion: typeof o.suggestion === "string" ? o.suggestion : "",
      reviewStatus,
      text: paragraphText.trim() ? paragraphText : undefined,
      analysis: typeof analysis === "string" ? analysis : undefined,
      wordChanges,
    };
  });
}

/** Builds final paragraph text from `word_changes` when `suggestion` is empty (equal + insertion only). */
export function buildTargetTextFromWordChanges(changes: AnalysisWordChange[]): string {
  let out = "";
  for (const c of changes) {
    if (c.type === "equal" || c.type === "insertion") {
      out += c.text;
    }
  }
  return out;
}

/** Reconstructs the analyzed paragraph (source) from API `word_changes` (equal + deletion). */
export function buildSourceTextFromWordChanges(changes: AnalysisWordChange[] | undefined): string {
  if (!changes?.length) {
    return "";
  }
  let out = "";
  for (const c of changes) {
    if (c.type === "equal" || c.type === "deletion") {
      out += c.text;
    }
  }
  return out;
}

function paragraphTextsEquivalent(a: string, b: string): boolean {
  return normalizeParagraphTextForCompare(a) === normalizeParagraphTextForCompare(b);
}

function normalizeParagraphTextForCompare(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/** Plain suggestion text for copy, edit pre-fill, and insert fallback. */
export function resolveSuggestionPlainText(row: AnalysisParagraphResult): string {
  if (row.suggestion.trim()) {
    return row.suggestion;
  }
  if (row.wordChanges?.length) {
    return buildTargetTextFromWordChanges(row.wordChanges);
  }
  return "";
}

/** Tokenize like contract-hub `text_diff._tokenize` (whitespace, words, punctuation). */
function tokenizeForWordDiff(text: string): string[] {
  const re = /\s+|\w+|[^\w\s]/g;
  const tokens: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    tokens.push(m[0]);
  }
  return tokens;
}

function joinDiffTokens(tokens: string[]): string {
  return tokens.join("");
}

/**
 * Token-level diff aligned with contract-hub `get_word_changes` for granular Word tracked changes.
 */
export function getWordChanges(oldText: string, newText: string): AnalysisWordChange[] {
  const tokens1 = tokenizeForWordDiff(oldText);
  const tokens2 = tokenizeForWordDiff(newText);
  const n = tokens1.length;
  const m = tokens2.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        tokens1[i] === tokens2[j]
          ? 1 + lcs[i + 1][j + 1]
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  type RawOp = { tag: "equal" | "delete" | "insert"; i: number; j: number };
  const raw: RawOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && tokens1[i] === tokens2[j]) {
      raw.push({ tag: "equal", i, j });
      i++;
      j++;
    } else if (i < n && (j >= m || lcs[i + 1][j] >= lcs[i][j + 1])) {
      raw.push({ tag: "delete", i, j });
      i++;
    } else {
      raw.push({ tag: "insert", i, j });
      j++;
    }
  }

  const result: AnalysisWordChange[] = [];
  let aRunStart = 0;
  let bRunStart = 0;
  let runTag: RawOp["tag"] | null = null;

  const flushRun = (endI: number, endJ: number) => {
    if (runTag === null) {
      return;
    }
    if (runTag === "equal") {
      result.push({ type: "equal", text: joinDiffTokens(tokens1.slice(aRunStart, endI)) });
    } else if (runTag === "delete") {
      result.push({ type: "deletion", text: joinDiffTokens(tokens1.slice(aRunStart, endI)) });
    } else {
      result.push({ type: "insertion", text: joinDiffTokens(tokens2.slice(bRunStart, endJ)) });
    }
    runTag = null;
  };

  for (const op of raw) {
    if (runTag === null) {
      runTag = op.tag;
      aRunStart = op.i;
      bRunStart = op.j;
      continue;
    }
    if (op.tag === runTag) {
      continue;
    }
    flushRun(op.i, op.j);
    runTag = op.tag;
    aRunStart = op.i;
    bRunStart = op.j;
  }
  flushRun(n, m);
  return result;
}

function wordChangesHaveMutations(changes: AnalysisWordChange[]): boolean {
  return changes.some((c) => c.type === "deletion" || c.type === "insertion");
}

/** Pick granular diff ops for Word apply (edited text recomputes diff from source paragraph). */
export function resolveWordChangesForApply(
  row: AnalysisParagraphResult,
  targetText: string,
  sourceText: string | undefined,
  options?: { overrideText?: string; reapply?: boolean }
): AnalysisWordChange[] | null {
  if (options?.overrideText !== undefined || options?.reapply) {
    const source = (
      sourceText?.trim() ||
      buildSourceTextFromWordChanges(row.wordChanges).trim() ||
      row.text?.trim() ||
      ""
    );
    const computed = getWordChanges(source, targetText);
    return wordChangesHaveMutations(computed) ? computed : null;
  }
  if (row.wordChanges?.length && wordChangesHaveMutations(row.wordChanges)) {
    return row.wordChanges;
  }
  return null;
}

/** Collapses whitespace for comparing paragraph text after edits. */
function normalizeParagraphText(s: string): string {
  return normalizeParagraphTextForCompare(s);
}

/**
 * Finds the body paragraph index to edit: prefer `paragraphIndex` if text still matches `expectedText`,
 * otherwise scan by `expectedText` (handles inserts/deletes above the paragraph in arbitrary documents).
 */
function resolveParagraphIndexForReplace(
  items: Word.Paragraph[],
  paragraphIndex: number,
  expectedText: string | undefined
): number {
  if (!expectedText || !normalizeParagraphText(expectedText)) {
    if (paragraphIndex < 0 || paragraphIndex >= items.length) {
      throw new Error(`Paragraph index ${paragraphIndex} is out of range (document has ${items.length} paragraphs).`);
    }
    return paragraphIndex;
  }

  const expected = normalizeParagraphText(expectedText);
  if (
    paragraphIndex >= 0 &&
    paragraphIndex < items.length &&
    normalizeParagraphText(items[paragraphIndex].text ?? "") === expected
  ) {
    return paragraphIndex;
  }

  for (let i = 0; i < items.length; i++) {
    if (normalizeParagraphText(items[i].text ?? "") === expected) {
      return i;
    }
  }

  throw new Error(
    "Could not find that paragraph in the document — it may have been edited. Re-run analysis or apply manually."
  );
}

export type ReplaceBodyParagraphOptions = {
  /** Text of the paragraph at analysis time; enables lookup if the paragraph moved. */
  expectedText?: string;
  /** Select edited location to bring it into view in Word. */
  revealSelection?: boolean;
};

export type RevealBodyParagraphOptions = {
  /** Text of the paragraph at analysis time; enables lookup if the paragraph moved. */
  expectedText?: string;
  /** Word selection mode; default highlights the full paragraph range. */
  selectionMode?: Word.SelectionMode;
};

/**
 * Scrolls to a body paragraph and selects it. Pass `expectedText` so the add-in can still find the
 * paragraph after the document shifts.
 */
export async function revealBodyParagraph(
  paragraphIndex: number,
  options?: RevealBodyParagraphOptions
): Promise<void> {
  const { expectedText, selectionMode } = options ?? {};
  try {
    await Word.run(async (context) => {
      const paras = context.document.body.paragraphs;
      paras.load("items");
      await context.sync();

      const expected = expectedText ? normalizeParagraphText(expectedText) : "";

      if (paragraphIndex >= 0 && paragraphIndex < paras.items.length) {
        if (expected) {
          paras.items[paragraphIndex].load("text");
          await context.sync();
          if (normalizeParagraphText(paras.items[paragraphIndex].text ?? "") === expected) {
            paras.items[paragraphIndex].getRange().select(selectionMode);
            await context.sync();
            return;
          }
        } else {
          paras.items[paragraphIndex].getRange().select(selectionMode);
          await context.sync();
          return;
        }
      }

      for (const p of paras.items) {
        p.load("text");
      }
      await context.sync();

      const idx = resolveParagraphIndexForReplace(paras.items, paragraphIndex, expectedText);
      paras.items[idx].getRange().select(selectionMode);
      await context.sync();
    });
  } catch (err) {
    throw new Error(formatHostError(err));
  }
}

/**
 * Replaces a body paragraph’s text. Pass `expectedText` (from analysis snapshot or API) so the add-in
 * can still find the paragraph after the document shifts.
 */
export async function replaceBodyParagraphText(
  paragraphIndex: number,
  newText: string,
  options?: ReplaceBodyParagraphOptions
): Promise<void> {
  try {
    await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const paras = context.document.body.paragraphs;
      paras.load("items");
      await context.sync();
      for (const p of paras.items) {
        p.load("text");
      }
      await context.sync();

      const idx = resolveParagraphIndexForReplace(
        paras.items,
        paragraphIndex,
        options?.expectedText
      );
      const range = paras.items[idx].getRange();
      if (options?.revealSelection) {
        range.select();
        await context.sync();
      }
      const inserted = range.insertText(newText, Word.InsertLocation.replace);
      if (options?.revealSelection) {
        inserted.select();
      }
      await context.sync();
    });
  } catch (err) {
    throw new Error(formatHostError(err));
  }
}

function sanitizeWordChangeText(text: string): string {
  return text.replace(/\u0005/g, "").replace(/\r/g, "");
}

/** Word `Range.search` throws `SearchStringInvalidOrTooLong` beyond this length (see Office JS #31). */
const WORD_RANGE_SEARCH_MAX_LENGTH = 255;

function searchTailFromCursor(
  cursor: Word.Range,
  paragraphEnd: Word.Range,
  rawSegment: string
): Word.RangeCollection {
  const segment = sanitizeWordChangeText(rawSegment);
  const tail = cursor.expandTo(paragraphEnd);
  let found = tail.search(segment, {
    matchCase: true,
    matchWholeWord: false,
    ignorePunct: false,
    ignoreSpace: false,
  });
  found.load("items");
  return found;
}

function searchTailRelaxed(
  cursor: Word.Range,
  paragraphEnd: Word.Range,
  rawSegment: string
): Word.RangeCollection {
  const segment = sanitizeWordChangeText(rawSegment);
  const tail = cursor.expandTo(paragraphEnd);
  const found = tail.search(segment, {
    matchCase: false,
    matchWholeWord: false,
    ignorePunct: false,
    ignoreSpace: true,
  });
  found.load("items");
  return found;
}

/**
 * Searches for `searchText` from `cursor` to `paragraphEnd`, trying strict then relaxed.
 * Returns the first matched range or throws with `errorLabel` context.
 */
async function searchOrThrow(
  context: Word.RequestContext,
  cursor: Word.Range,
  paragraphEnd: Word.Range,
  searchText: string,
  errorLabel: string
): Promise<Word.Range> {
  let found = searchTailFromCursor(cursor, paragraphEnd, searchText);
  await context.sync();
  if (found.items.length === 0) {
    found = searchTailRelaxed(cursor, paragraphEnd, searchText);
    await context.sync();
  }
  if (found.items.length === 0) {
    throw new Error(errorLabel);
  }
  return found.items[0];
}

/**
 * For segments exceeding the 255-char search limit, locate the range by searching for
 * a prefix and suffix separately and combining them via `expandTo`.
 */
async function searchOverlongRange(
  context: Word.RequestContext,
  cursor: Word.Range,
  paragraphEnd: Word.Range,
  segment: string,
  errorLabel: string
): Promise<Word.Range> {
  const prefixText = segment.slice(0, WORD_RANGE_SEARCH_MAX_LENGTH);
  const suffixText = segment.slice(-Math.min(200, WORD_RANGE_SEARCH_MAX_LENGTH));

  const prefixMatch = await searchOrThrow(context, cursor, paragraphEnd, prefixText, errorLabel);
  const suffixMatch = await searchOrThrow(
    context,
    prefixMatch.getRange(Word.RangeLocation.start),
    paragraphEnd,
    suffixText,
    errorLabel
  );

  const fullRange = prefixMatch.expandTo(suffixMatch);
  return fullRange;
}

/**
 * Applies API `word_changes` in document order using a running cursor (tail search).
 * `equal` only advances the cursor — it does not modify text. Insertions/deletions create granular revisions.
 */
export async function applyBodyParagraphWordChanges(
  paragraphIndex: number,
  suggestionText: string,
  wordChanges: AnalysisWordChange[],
  options?: ReplaceBodyParagraphOptions
): Promise<void> {
  if (!wordChanges.length) {
    await replaceBodyParagraphText(paragraphIndex, suggestionText, options);
    return;
  }
  try {
    await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const paras = context.document.body.paragraphs;
      paras.load("text");
      await context.sync();

      const idx = resolveParagraphIndexForReplace(paras.items, paragraphIndex, options?.expectedText);
      const para = paras.items[idx];
      if (options?.revealSelection) {
        para.getRange().select();
        await context.sync();
      }
      const paragraphEnd = para.getRange(Word.RangeLocation.end);
      let cursor = para.getRange(Word.RangeLocation.start);
      let appliedAnyChange = false;
      const hasMutations = wordChanges.some((c) => c.type === "deletion" || c.type === "insertion");

      for (const change of wordChanges) {
        const segment = sanitizeWordChangeText(change.text);
        const isOverlong = segment.length > WORD_RANGE_SEARCH_MAX_LENGTH;

        if (change.type === "equal") {
          if (!segment) {
            continue;
          }
          const searchText = isOverlong
            ? segment.slice(-WORD_RANGE_SEARCH_MAX_LENGTH)
            : change.text;
          const match = await searchOrThrow(
            context, cursor, paragraphEnd, searchText,
            "Could not match an equal segment from the AI diff. Re-run analysis or check the paragraph was unchanged."
          );
          cursor = match.getRange(Word.RangeLocation.end);
          continue;
        }

        if (change.type === "deletion") {
          if (!segment) {
            continue;
          }
          let delRange: Word.Range;
          if (isOverlong) {
            delRange = await searchOverlongRange(
              context, cursor, paragraphEnd, segment,
              "Could not locate text to delete. The document may differ from the analyzed version. Re-run analysis."
            );
          } else {
            delRange = await searchOrThrow(
              context, cursor, paragraphEnd, change.text,
              "Could not locate text to delete. The document may differ from the analyzed version. Re-run analysis."
            );
          }
          delRange.insertText("", Word.InsertLocation.replace);
          await context.sync();
          cursor = delRange.getRange(Word.RangeLocation.start);
          appliedAnyChange = true;
          continue;
        }

        if (!segment) {
          continue;
        }
        const inserted = cursor.insertText(segment, Word.InsertLocation.start);
        await context.sync();
        cursor = inserted.getRange(Word.RangeLocation.end);
        appliedAnyChange = true;
      }

      if (hasMutations && !appliedAnyChange) {
        throw new Error("No edits were applied from word_changes. Re-run analysis or apply manually.");
      }
      if (options?.revealSelection) {
        para.getRange().select();
      }
      await context.sync();
    });
  } catch (err) {
    throw new Error(formatHostError(err));
  }
}

/** `index → text` map from section grouping (same content as flat POST payload). */
export function buildParagraphTextSnapshot(sections: LegalDocumentSection[]): Record<number, string> {
  return paragraphChunksToSnapshot(flattenDocumentParagraphs(sections));
}

export type RunAddInAnalysisResult = {
  raw: unknown;
  /** Captured when analysis ran; use with Accept to locate text if indices shifted. */
  paragraphTextByIndex: Record<number, string>;
};

/**
 * POSTs `{ paragraphs: [...], agreement_version_id }` to `POST /contracts/add_in/ai_analysis`.
 */
export async function postAddInAnalysis(
  agreementVersionId: string,
  payload: DocumentParagraphChunk[],
  onProgress?: ProgressCallback
): Promise<unknown> {
  onProgress?.("Sending analysis…");
  const response = await fetchApi(AGREEMENT_ADD_IN_AI_ANALYSIS_POST_PATH, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    body: JSON.stringify({ paragraphs: payload, agreement_version_id: agreementVersionId }),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail ? `Request failed (${response.status}): ${detail}` : `Request failed (${response.status})`);
  }
  const raw = await response.text();
  if (!raw.trim()) {
    return null;
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

export type AcceptedAiSuggestion = {
  id: string;
  suggestion: string;
};

/**
 * POST /contracts/bulk/change — report accepted / rejected AI change UUIDs back to the server.
 */
export async function postBulkAiChange(
  agreementVersionId: string,
  acceptedIds: string[],
  rejectedIds: string[],
  acceptedSuggestions?: AcceptedAiSuggestion[]
): Promise<void> {
  const body: Record<string, unknown> = {
    agreement_version_id: agreementVersionId,
    accepted_ai_change: acceptedIds,
    rejected_ai_change: rejectedIds,
  };
  if (acceptedSuggestions && acceptedSuggestions.length > 0) {
    body.accepted_suggestions = acceptedSuggestions;
  }
  const response = await fetchApi(AGREEMENT_BULK_CHANGE_PATH, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail ? `Bulk change failed (${response.status}): ${detail}` : `Bulk change failed (${response.status})`);
  }
}

export async function runAddInAnalysis(onProgress?: ProgressCallback): Promise<RunAddInAnalysisResult> {
  try {
    onProgress?.("Reading AgreementVersionId from document…");
    const agreementVersionId = await getAgreementVersionIdFromDocument();
    onProgress?.("Reading document…");
    const sections = await extractLegalDocument();
    const flat = flattenDocumentParagraphs(sections);
    const paragraphTextByIndex = paragraphChunksToSnapshot(flat);
    if (flat.length === 0) {
      onProgress?.("No paragraph text found — add headings or body text.");
    }
    const raw = await postAddInAnalysis(agreementVersionId, flat, onProgress);
    return { raw, paragraphTextByIndex };
  } catch (err) {
    throw new Error(formatHostError(err));
  }
}

function findCustomPropertyByKeyLower(
  items: Word.CustomProperty[],
  keyLower: string
): { key: string; value: string } | undefined {
  for (const p of items) {
    const key = String(p.key ?? "").trim();
    if (key.toLowerCase() === keyLower) {
      return { key, value: String(p.value ?? "").trim() };
    }
  }
  return undefined;
}

function findAgreementIdProperty(
  items: Word.CustomProperty[]
): { key: string; value: string } | undefined {
  return findCustomPropertyByKeyLower(items, "agreementid");
}

async function loadCustomProperties(context: Word.RequestContext): Promise<Word.CustomProperty[]> {
  const customProps = context.document.properties.customProperties;
  customProps.load("items");
  await context.sync();
  for (const p of customProps.items) {
    p.load(["key", "value"]);
  }
  await context.sync();
  return customProps.items;
}

/** Custom doc props: AgreementVersionId, agreement_version_id (normalized key match). */
function findAgreementVersionIdProperty(
  items: Word.CustomProperty[]
): { key: string; value: string } | undefined {
  for (const p of items) {
    const raw = String(p.key ?? "").trim();
    const k = raw.toLowerCase().replace(/\s+/g, "");
    if (k === "agreementversionid" || k === "agreement_version_id") {
      const v = String(p.value ?? "").trim();
      if (v) {
        return { key: raw, value: v };
      }
    }
  }
  return undefined;
}

export type AgreementIdentifiersFromDocument = {
  agreementId: string;
  /**
   * Version-scoped id for summary, upload, compare APIs.
   * From AgreementVersionId / agreement_version_id when set; otherwise same as agreementId.
   */
  agreementVersionId: string;
};

/**
 * Whether the active document has a non-empty Cenna ``AgreementId`` custom property.
 * Does not throw on missing property; returns ``"error"`` only if the Word API fails.
 */
export async function checkStampedAgreementIdInDocument(): Promise<"present" | "absent" | "error"> {
  try {
    return await Word.run(async (context) => {
      const items = await loadCustomProperties(context);
      const agreement = findAgreementIdProperty(items);
      if (!agreement?.value?.trim()) {
        return "absent";
      }
      return "present";
    });
  } catch {
    return "error";
  }
}

export type AgreementDocumentSupport = "supported" | "unsupported";

/**
 * Whether the open document is a Cenna contract this add-in session can use:
 * stamped ``AgreementId`` in Word and the version exists for the logged-in Contract Hub project.
 */
export async function checkAgreementDocumentSupport(): Promise<AgreementDocumentSupport> {
  const stamped = await checkStampedAgreementIdInDocument();
  if (stamped !== "present") {
    return "unsupported";
  }

  try {
    const { agreementVersionId } = await getAgreementIdentifiersFromDocument();
    const trimmed = agreementVersionId.trim();
    if (!trimmed) {
      return "unsupported";
    }

    const response = await fetchApi(
      `${AGREEMENT_SUMMARY_PATH_BASE}/${encodeURIComponent(trimmed)}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${requireAccessToken()}` },
      }
    );
    if (response.ok) {
      return "supported";
    }
    if (response.status === 404) {
      return "unsupported";
    }
    return "supported";
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("AgreementId custom property not found")) {
      return "unsupported";
    }
    return "supported";
  }
}

/** Reads custom properties once; version id defaults to agreement id if not stamped separately. */
export async function getAgreementIdentifiersFromDocument(): Promise<AgreementIdentifiersFromDocument> {
  return Word.run(async (context) => {
    const items = await loadCustomProperties(context);
    const agreement = findAgreementIdProperty(items);
    if (!agreement?.value) {
      throw new Error("AgreementId custom property not found in this document.");
    }
    const versionProp = findAgreementVersionIdProperty(items);
    const agreementVersionId = versionProp?.value || agreement.value;
    return {
      agreementId: agreement.value,
      agreementVersionId,
    };
  });
}

export async function getAgreementIdFromDocument(): Promise<string> {
  const { agreementId } = await getAgreementIdentifiersFromDocument();
  return agreementId;
}

export async function getAgreementVersionIdFromDocument(): Promise<string> {
  const { agreementVersionId } = await getAgreementIdentifiersFromDocument();
  return agreementVersionId;
}

async function readJsonResponseBody(response: Response): Promise<unknown> {
  const raw = await response.text();
  if (!raw.trim()) {
    return null;
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

/**
 * GET saved AI analysis for a version (same JSON shape as POST analysis response).
 * Returns `null` when the server responds 404 (no saved analysis).
 */
export async function fetchStoredAiAnalysis(
  agreementVersionId: string,
  onProgress?: ProgressCallback,
  signal?: AbortSignal
): Promise<unknown | null> {
  onProgress?.("Loading saved analysis…");
  const response = await fetchApi(agreementAiAnalysisGetPath(agreementVersionId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    signal,
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail ? `Request failed (${response.status}): ${detail}` : `Request failed (${response.status})`);
  }
  return readJsonResponseBody(response);
}

/**
 * `GET /contracts/changes/{agreement_version_id}` — paragraph-level revisions (see `mock/changes.json`).
 */
export async function fetchAgreementChanges(
  agreementVersionId: string,
  signal?: AbortSignal
): Promise<AgreementChangesResponse> {
  const response = await fetchApi(agreementChangesGetPath(agreementVersionId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    signal,
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      detail ? `Changes request failed (${response.status}): ${detail}` : `Changes request failed (${response.status})`
    );
  }
  const data = await readJsonResponseBody(response);
  return parseAgreementChangesResponse(data);
}

/**
 * Deletes saved AI analysis for a version before a fresh run.
 * Must succeed so re-run never leaves stale insights for removed paragraph indexes.
 */
export async function deleteAgreementAiAnalysisForVersion(
  agreementVersionId: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetchApi(agreementAiAnalysisDeletePath(agreementVersionId), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    signal,
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      detail
        ? `Could not clear previous AI analysis (${response.status}): ${detail}`
        : `Could not clear previous AI analysis (${response.status})`
    );
  }
}

export type AiAnalysisJobStatus = "pending" | "running" | "completed" | "failed" | "cancelled";

export type AiAnalysisJobProgress = {
  total: number;
  completed: number;
  currentIndex: number | null;
  currentSnippet: string | null;
};

export type AiAnalysisJobSnapshot = {
  jobId: string | null;
  versionId: string;
  status: AiAnalysisJobStatus;
  progress: AiAnalysisJobProgress | null;
  errorMessage: string | null;
  tokensUsed: number;
  noChanges: boolean;
};

export const AI_ANALYSIS_JOB_POLL_INTERVAL_MS = 1500;

const AI_ANALYSIS_JOB_ACTIVE = new Set<AiAnalysisJobStatus>(["pending", "running"]);
const AI_ANALYSIS_JOB_TERMINAL = new Set<AiAnalysisJobStatus>([
  "completed",
  "failed",
  "cancelled",
]);

export function isAiAnalysisJobActive(status: AiAnalysisJobStatus): boolean {
  return AI_ANALYSIS_JOB_ACTIVE.has(status);
}

export function isAiAnalysisJobTerminal(status: AiAnalysisJobStatus): boolean {
  return AI_ANALYSIS_JOB_TERMINAL.has(status);
}

function parseAiAnalysisJobStatus(raw: unknown): AiAnalysisJobStatus {
  if (
    raw === "pending" ||
    raw === "running" ||
    raw === "completed" ||
    raw === "failed" ||
    raw === "cancelled"
  ) {
    return raw;
  }
  return "failed";
}

function parseAiAnalysisJobProgress(raw: unknown): AiAnalysisJobProgress | null {
  if (raw === null || typeof raw !== "object") {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const total = typeof o.total === "number" ? o.total : Number(o.total) || 0;
  const completed = typeof o.completed === "number" ? o.completed : Number(o.completed) || 0;
  const currentIndexRaw = o.current_index ?? o.currentIndex;
  let currentIndex: number | null = null;
  if (typeof currentIndexRaw === "number" && Number.isFinite(currentIndexRaw)) {
    currentIndex = currentIndexRaw;
  } else if (currentIndexRaw != null && currentIndexRaw !== "") {
    const n = Number(currentIndexRaw);
    currentIndex = Number.isFinite(n) ? n : null;
  }
  const snippetRaw = o.current_snippet ?? o.currentSnippet;
  const currentSnippet = typeof snippetRaw === "string" ? snippetRaw : null;
  return { total, completed, currentIndex, currentSnippet };
}

export function parseAiAnalysisJobSnapshot(data: unknown): AiAnalysisJobSnapshot {
  if (data === null || typeof data !== "object") {
    return {
      jobId: null,
      versionId: "",
      status: "failed",
      progress: null,
      errorMessage: "Invalid job response",
      tokensUsed: 0,
      noChanges: false,
    };
  }
  const o = data as Record<string, unknown>;
  const jobIdRaw = o.job_id ?? o.jobId;
  const jobId = typeof jobIdRaw === "string" && jobIdRaw ? jobIdRaw : null;
  const versionId = typeof o.version_id === "string" ? o.version_id : String(o.versionId ?? "");
  return {
    jobId,
    versionId,
    status: parseAiAnalysisJobStatus(o.status),
    progress: parseAiAnalysisJobProgress(o.progress),
    errorMessage: typeof o.error_message === "string" ? o.error_message : typeof o.errorMessage === "string" ? o.errorMessage : null,
    tokensUsed: typeof o.tokens_used === "number" ? o.tokens_used : Number(o.tokensUsed) || 0,
    noChanges: o.no_changes === true || o.noChanges === true,
  };
}

/** Human-readable progress line for the AI analysis status strip. */
export function formatAiAnalysisJobProgress(job: AiAnalysisJobSnapshot): string {
  if (job.noChanges) {
    return "No paragraph changes found to analyse for this version.";
  }
  if (job.status === "pending") {
    return "Starting AI analysis…";
  }
  if (job.status === "failed") {
    return job.errorMessage?.trim() || "AI analysis failed.";
  }
  if (job.status === "completed") {
    return "";
  }
  if (job.status === "cancelled") {
    return "";
  }
  const p = job.progress;
  if (!p || p.total <= 0) {
    return "Running AI analysis…";
  }
  const step = Math.min(Math.max(p.completed, p.currentSnippet ? p.completed + 1 : p.completed), p.total);
  const displayStep = Math.max(step, 1);
  const snippet = p.currentSnippet?.trim();
  if (snippet) {
    return `Running AI analysis on: ${snippet} (${displayStep} of ${p.total})`;
  }
  return `Running AI analysis… (${p.completed} of ${p.total})`;
}

/**
 * `POST /contracts/ai_analysis/{version_id}/jobs` — start a background analysis job.
 * Returns immediately with job snapshot (or ``noChanges`` when there is nothing to analyse).
 */
export async function startAiAnalysisJob(
  agreementVersionId?: string,
  signal?: AbortSignal
): Promise<AiAnalysisJobSnapshot> {
  const versionId = agreementVersionId ?? (await getAgreementVersionIdFromDocument());
  const response = await fetchApi(agreementAiAnalysisJobsStartPath(versionId), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    signal,
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      detail
        ? `Could not start AI analysis (${response.status}): ${detail}`
        : `Could not start AI analysis (${response.status})`
    );
  }
  const data = await readJsonResponseBody(response);
  return parseAiAnalysisJobSnapshot(data);
}

/**
 * `GET /contracts/ai_analysis/{version_id}/jobs/active` — active job or ``null`` when none.
 */
export async function fetchActiveAiAnalysisJob(
  agreementVersionId?: string,
  signal?: AbortSignal
): Promise<AiAnalysisJobSnapshot | null> {
  const versionId = agreementVersionId ?? (await getAgreementVersionIdFromDocument());
  const response = await fetchApi(agreementAiAnalysisJobsActivePath(versionId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    signal,
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      detail
        ? `Active AI analysis job request failed (${response.status}): ${detail}`
        : `Active AI analysis job request failed (${response.status})`
    );
  }
  const data = await readJsonResponseBody(response);
  return parseAiAnalysisJobSnapshot(data);
}

/**
 * `GET /contracts/ai_analysis/jobs/{job_id}` — poll job status.
 */
export async function fetchAiAnalysisJob(
  jobId: string,
  signal?: AbortSignal
): Promise<AiAnalysisJobSnapshot> {
  const response = await fetchApi(agreementAiAnalysisJobGetPath(jobId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    signal,
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      detail
        ? `AI analysis job request failed (${response.status}): ${detail}`
        : `AI analysis job request failed (${response.status})`
    );
  }
  const data = await readJsonResponseBody(response);
  return parseAiAnalysisJobSnapshot(data);
}

/**
 * `POST /contracts/paragraph/ai_analysis/{agreement_version_id}` — body is one {@link AgreementChangeItem} (unchanged).
 * Response matches the add-in analysis shape (`paragraphs` array); each call returns cumulative results.
 */
export async function postParagraphAiAnalysis(
  agreementVersionId: string,
  changeItem: AgreementChangeItem,
  signal?: AbortSignal
): Promise<unknown> {
  const response = await fetchApi(
    agreementParagraphAiAnalysisPostPath(agreementVersionId),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${requireAccessToken()}`,
      },
      body: JSON.stringify(changeItem),
      signal,
    }
  );
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      detail
        ? `Paragraph AI analysis failed (${response.status}): ${detail}`
        : `Paragraph AI analysis failed (${response.status})`
    );
  }
  const raw = await response.text();
  if (!raw.trim()) {
    return { paragraphs: [] };
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

/**
 * Re-runs AI analysis for a single paragraph (tracked change). Fetches current changes,
 * posts one paragraph analysis call, and returns the cumulative saved results.
 */
export async function retryParagraphAiAnalysis(
  paragraphIndex: number,
  signal?: AbortSignal
): Promise<RunAddInAnalysisResult> {
  const agreementVersionId = await getAgreementVersionIdFromDocument();
  const changesRes = await fetchAgreementChanges(agreementVersionId, signal);
  const item = changesRes.changes?.find((c) => c.index === paragraphIndex);
  if (!item) {
    throw new Error(
      `No tracked change found for paragraph ${paragraphIndex}. Run full analysis or refresh changes.`
    );
  }
  const raw = await postParagraphAiAnalysis(agreementVersionId, item, signal);
  const paragraphTextByIndex = buildParagraphSnapshotFromAgreementChanges(changesRes.changes ?? []);
  return { raw, paragraphTextByIndex };
}

/** Source paragraph text for Word apply — never the AI suggestion. */
export function resolveParagraphSourceText(
  row: AnalysisParagraphResult,
  paragraphSnapshot: Record<number, string> | null | undefined
): string | undefined {
  const suggestion = resolveSuggestionPlainText(row);

  const fromSnapshot = paragraphSnapshot?.[row.index]?.trim();
  if (fromSnapshot && (!suggestion || !paragraphTextsEquivalent(fromSnapshot, suggestion))) {
    return fromSnapshot;
  }

  const fromRow = row.text?.trim();
  if (fromRow && (!suggestion || !paragraphTextsEquivalent(fromRow, suggestion))) {
    return fromRow;
  }

  const fromDiff = buildSourceTextFromWordChanges(row.wordChanges).trim();
  if (fromDiff && (!suggestion || !paragraphTextsEquivalent(fromDiff, suggestion))) {
    return fromDiff;
  }

  return fromSnapshot || fromRow || fromDiff || undefined;
}

export type RunParagraphAiAnalysisOptions = {
  signal?: AbortSignal;
  /** Fired once after loading changes, before sequential POSTs (for Apply snapshot). */
  onChangesReady?: (paragraphTextByIndex: Record<number, string>) => void;
  /** Fired after each POST with cumulative parsed rows. */
  onCumulativeResult?: (parsed: AnalysisParagraphResult[], raw: unknown) => void;
};

/**
 * @deprecated Prefer {@link startAiAnalysisJob} + job polling. Kept for reference /
 * emergency fallback: clears analysis, loads changes, POSTs each change sequentially.
 */
export async function runParagraphAiAnalysis(
  onProgress?: ProgressCallback,
  options?: RunParagraphAiAnalysisOptions
): Promise<RunAddInAnalysisResult> {
  const { signal, onChangesReady, onCumulativeResult } = options ?? {};
  try {
    onProgress?.("Reading AgreementVersionId from document…");
    const agreementVersionId = await getAgreementVersionIdFromDocument();

    onProgress?.("Clearing previous AI analysis…");
    await deleteAgreementAiAnalysisForVersion(agreementVersionId, signal);

    onProgress?.("Loading paragraph changes…");
    const changesRes = await fetchAgreementChanges(agreementVersionId, signal);
    const items = changesRes.changes ?? [];
    const paragraphTextByIndex = buildParagraphSnapshotFromAgreementChanges(items);
    onChangesReady?.(paragraphTextByIndex);
    if (items.length === 0) {
      onProgress?.("No paragraph changes found for this agreement.");
      // Distinguish empty-changes from a successful run that returned no insights,
      // so the UI can show a clear message instead of silently resetting.
      return { raw: { paragraphs: [], no_changes: true }, paragraphTextByIndex };
    }
    let lastRaw: unknown = { paragraphs: [] };
    const n = items.length;
    for (let i = 0; i < n; i++) {
      if (signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      const item = items[i];
      const snippet = trimProgressText(item.text ?? "");
      onProgress?.(`Running AI analysis on: ${snippet} (${i + 1} of ${n})`);
      lastRaw = await postParagraphAiAnalysis(agreementVersionId, item, signal);
      const parsed = parseAddInAnalysisResponse(lastRaw);
      onCumulativeResult?.(parsed, lastRaw);
    }
    onProgress?.("");
    return { raw: lastRaw, paragraphTextByIndex };
  } catch (err) {
    if (isAbortError(err)) {
      throw err;
    }
    throw new Error(formatHostError(err));
  }
}

/**
 * Tab open / refresh only: GET saved AI analysis for the version. Does not call the changes API
 * or run paragraph analysis — use {@link runParagraphAiAnalysis} for that.
 * Paragraph snapshot for Apply is derived from saved `paragraphs` when present.
 */
export async function loadAiAnalysisFromServer(
  onProgress?: ProgressCallback,
  signal?: AbortSignal
): Promise<RunAddInAnalysisResult> {
  try {
    onProgress?.("Reading AgreementVersionId from document…");
    const agreementVersionId = await getAgreementVersionIdFromDocument();
    onProgress?.("Checking for saved AI analysis…");
    const raw = await fetchStoredAiAnalysis(agreementVersionId, undefined, signal);
    const normalized = raw ?? { paragraphs: [] };
    const parsed = parseAddInAnalysisResponse(normalized);
    const paragraphTextByIndex = buildParagraphSnapshotFromAnalysisResults(parsed);
    return { raw: normalized, paragraphTextByIndex };
  } catch (err) {
    if (isAbortError(err)) {
      throw err;
    }
    throw new Error(formatHostError(err));
  }
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString();
}

export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/** Word often reports only "GeneralException"; use debugInfo from OfficeExtension.Error when present. */
export function formatHostError(err: unknown): string {
  if (!(err instanceof Error)) {
    return String(err);
  }
  const oe = err as OfficeExtension.Error;
  const di = oe.debugInfo;
  if (di?.message) {
    const parts: string[] = [di.message];
    if (di.errorLocation) {
      parts.push(`(${di.errorLocation})`);
    }
    if (oe.code === "GeneralException" && di.statement) {
      parts.push(di.statement);
    }
    return parts.join(" ");
  }
  return oe.message || String(err);
}

/**
 * Turns on Track Changes for all editors (WordApi 1.4+). On desktop Word, if the window is in Reading view or reading layout, switches to Print Layout, then shows all revision markup.
 * Safe to call on load; failures are ignored (e.g. Word on the web has no activeWindow.view).
 */
export async function enableDocumentReviewMode(): Promise<void> {
  try {
    await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await context.sync();
    });
  } catch (err) {
    console.warn("[Cenna] enableDocumentReviewMode (track changes):", formatHostError(err));
  }

  try {
    await Word.run(async (context) => {
      const view = context.document.activeWindow.view;
      view.load(["type", "isInReadingLayout"]);
      await context.sync();
      // Reading / "View" mode does not behave like Review; Print Layout shows markup reliably.
      if (view.type === Word.ViewType.reading || view.isInReadingLayout) {
        view.type = Word.ViewType.print;
        view.isInReadingLayout = false;
      }
      view.revisionsFilter.markup = Word.RevisionsMarkup.all;
      view.areFormatChangesDisplayed = true;
      view.areRevisionsAndCommentsDisplayed = true;
      await context.sync();
    });
  } catch {
    // Word on the web / hosts without WordApiDesktop activeWindow.view
  }
}

function getDocumentFileAsync(): Promise<Office.File> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4 * 1024 * 1024 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error.message));
          return;
        }
        resolve(result.value);
      }
    );
  });
}

function sliceDataToArrayBuffer(data: unknown): ArrayBuffer {
  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data).slice().buffer;
  }
  if (data instanceof Uint8Array) {
    return data.slice().buffer;
  }
  if (Array.isArray(data)) {
    return new Uint8Array(data).slice().buffer;
  }
  return new Uint8Array(data as ArrayLike<number>).slice().buffer;
}

function getSliceAsync(file: Office.File, sliceIndex: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    file.getSliceAsync(sliceIndex, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error.message));
        return;
      }
      resolve(sliceDataToArrayBuffer(result.value.data));
    });
  });
}

function closeFileAsync(file: Office.File): Promise<void> {
  return new Promise((resolve, reject) => {
    file.closeAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error.message));
        return;
      }
      resolve();
    });
  });
}

export async function readCompressedDocumentBytes(): Promise<ArrayBuffer> {
  const file = await getDocumentFileAsync();
  try {
    const parts: Uint8Array[] = [];
    for (let i = 0; i < file.sliceCount; i++) {
      const slice = await getSliceAsync(file, i);
      parts.push(new Uint8Array(slice));
    }
    const total = parts.reduce((acc, p) => acc + p.byteLength, 0);
    const merged = new Uint8Array(total);
    let offset = 0;
    for (const p of parts) {
      merged.set(p, offset);
      offset += p.byteLength;
    }
    return merged.buffer;
  } finally {
    await closeFileAsync(file);
  }
}

function normalizeDocumentBase64(value: string): string {
  const trimmed = value.trim();
  const dataUrl = /^data:[^;]+;base64,(.+)$/i.exec(trimmed);
  if (dataUrl) {
    return dataUrl[1].replace(/\s/g, "");
  }
  return trimmed.replace(/\s/g, "");
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk) as unknown as number[]);
  }
  return btoa(binary);
}

function normalizeAuthorKey(name: string): string {
  return name.trim().toLowerCase();
}

/** `Word.Document.saved` is false when the file is new or has unsaved edits (WordApi 1.1). */
async function documentHasUnsavedChanges(): Promise<boolean> {
  return Word.run(async (context) => {
    await context.document.save();
    context.document.load("saved");
    await context.sync();
    return !context.document.saved;
  });
}

/**
 * One sync for clean-copy gating: `Word.Document.saved` is false when the file is new or has unsaved edits (WordApi 1.1).
 * Tracked changes use `getTrackedChanges()` (WordApi 1.6).
 */
async function preflightCleanCopyUpload(): Promise<"ok" | "unsaved" | "tracked"> {
  return Word.run(async (context) => {
    await context.document.save();
    context.document.load("saved");
    const tracked = context.document.body.getTrackedChanges();
    tracked.load("items");
    await context.sync();
    if (tracked.items.length > 0) {
      return "tracked";
    }
    if (!context.document.saved) {
      return "unsaved";
    }
    return "ok";
  });
}

async function collectPreCompareTrackedChangeAuthors(): Promise<Set<string>> {
  const authors = new Set<string>();
  try {
    await Word.run(async (context) => {
      const tracked = context.document.body.getTrackedChanges();
      tracked.load("items");
      await context.sync();
      for (const tc of tracked.items) {
        tc.load("author");
      }
      await context.sync();
      for (const tc of tracked.items) {
        const a = tc.author?.trim();
        if (a) {
          authors.add(a);
        }
      }
    });
  } catch {
    // Compare can still run; without authors every post-compare mark may be highlighted.
  }
  return authors;
}

export async function fetchAgreementSummary(agreementVersionId: string): Promise<AgreementSummaryResponse> {
  const trimmed = agreementVersionId.trim();
  if (!trimmed) {
    throw new Error("Missing agreementVersionId for summary.");
  }
  const response = await fetchApi(
    `${AGREEMENT_SUMMARY_PATH_BASE}/${encodeURIComponent(trimmed)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${requireAccessToken()}`,
      },
    }
  );
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail ? `Summary request failed (${response.status}): ${detail}` : `Summary request failed (${response.status})`);
  }
  const data = (await response.json()) as Record<string, unknown>;
  const summary = typeof data.summary === "string" ? data.summary : "";
  const cv = data.current_version;
  const currentVersion = typeof cv === "number" ? cv : Number(cv);
  const initiatedBy = typeof data.initiated_by === "string" ? data.initiated_by : null;
  return {
    summary,
    current_version: Number.isFinite(currentVersion) ? currentVersion : 0,
    initiatedBy: initiatedBy,
  };
}

function parseAiAnalysisPayload(data: Record<string, unknown>): AgreementAiAnalysisFromSummaryResponse {
  const aiAnalysis = typeof data.ai_analysis === "string" ? data.ai_analysis : null;
  const aiAnalysisActions =
    typeof data.ai_analysis_actions === "object" && data.ai_analysis_actions != null
      ? (data.ai_analysis_actions as { high: number; medium: number; low?: number })
      : { high: 0, medium: 0, low: 0 };
  return { aiAnalysis, aiAnalysisActions };
}

export async function fetchAgreementAiAnalysis(
  agreementVersionId: string,
  options?: { signal?: AbortSignal }
): Promise<AgreementAiAnalysisFromSummaryResponse> {
  const trimmed = agreementVersionId.trim();
  if (!trimmed) {
    throw new Error("Missing agreementVersionId for AI analysis.");
  }
  const path = `${AGREEMENT_SUMMARY_PATH_BASE}/ai_analysis/${encodeURIComponent(trimmed)}`;
  const response = await fetchApi(path, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    cache: "no-store",
    signal: options?.signal,
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      detail ? `AI analysis request failed (${response.status}): ${detail}` : `AI analysis request failed (${response.status})`
    );
  }
  const data = (await response.json()) as Record<string, unknown>;
  return parseAiAnalysisPayload(data);
}

/** Uses AgreementVersionId (or AgreementId fallback) for GET .../summary/ai_analysis/{agreementVersionId}. */
export async function fetchAgreementAiAnalysisForCurrentDocument(
  options?: { signal?: AbortSignal }
): Promise<AgreementAiAnalysisFromSummaryResponse> {
  const { agreementVersionId } = await getAgreementIdentifiersFromDocument();
  return fetchAgreementAiAnalysis(agreementVersionId, options);
}

/** Uses AgreementVersionId (or AgreementId fallback) for GET /summary/{agreementVersionId}. */
export async function fetchAgreementSummaryForCurrentDocument(): Promise<AgreementSummaryResponse> {
  const { agreementVersionId } = await getAgreementIdentifiersFromDocument();
  return fetchAgreementSummary(agreementVersionId);
}

export async function fetchAgreementVersions(onProgress?: ProgressCallback): Promise<VersionsResponse> {
  try {
    onProgress?.("Reading AgreementId from document…");
    const agreementId = await getAgreementIdFromDocument();
    onProgress?.("Loading versions…");
    const response = await fetchApi(
      `${AGREEMENT_VERSIONS_PATH_BASE}/${encodeURIComponent(agreementId)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${requireAccessToken()}`,
        },
        body: JSON.stringify({}),
      }
    );
    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }
    return (await response.json()) as VersionsResponse;
  } catch (err) {
    throw new Error(formatHostError(err));
  }
}

export type PromoteVersionResult = {
  version: {
    id: string;
    version: number;
    is_major: boolean;
    display_label: string;
    promoted_from_version_id?: string | null;
    promoted_from_label?: string | null;
  };
  superseded_count: number;
  next_major_version: number;
};

/** POST /contracts/{agreementId}/versions/{versionId}/promote */
export async function promoteAgreementVersion(
  versionId: string,
  onProgress?: ProgressCallback,
  uploadFilename?: string,
): Promise<PromoteVersionResult> {
  try {
    onProgress?.("Reading AgreementId from document…");
    const agreementId = await getAgreementIdFromDocument();
    onProgress?.("Promoting revision…");
    const trimmedName = uploadFilename?.trim();
    const response = await fetchApi(
      `/contracts/${encodeURIComponent(agreementId)}/versions/${encodeURIComponent(versionId)}/promote`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${requireAccessToken()}`,
        },
        body: JSON.stringify(trimmedName ? { upload_filename: trimmedName } : {}),
      },
    );
    if (!response.ok) {
      let detail = `Request failed (${response.status})`;
      try {
        const body = (await response.json()) as { detail?: unknown };
        if (typeof body.detail === "string") {
          detail = body.detail;
        }
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    return (await response.json()) as PromoteVersionResult;
  } catch (err) {
    throw new Error(formatHostError(err));
  }
}

function filenameFromContentDisposition(cd: string | null, fallback: string): string {
  if (!cd) return fallback;
  const star = /filename\*=(?:UTF-8'')?([^;\n]+)/i.exec(cd);
  const quoted = /filename="([^"]+)"/i.exec(cd);
  const plain = /filename=([^;\n]+)/i.exec(cd);
  const raw = (star?.[1] ?? quoted?.[1] ?? plain?.[1])?.trim()?.replace(/^["']|["']$/g, "");
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/** GET /contracts/{agreementId}/versions/{versionId}/download — stream DOCX to the browser. */
export async function downloadAgreementVersion(
  versionId: string,
  opts?: { suggestedBaseName?: string; onProgress?: ProgressCallback },
): Promise<void> {
  try {
    opts?.onProgress?.("Reading AgreementId from document…");
    const agreementId = await getAgreementIdFromDocument();
    opts?.onProgress?.("Downloading document…");
    const response = await fetchApi(
      `/contracts/${encodeURIComponent(agreementId)}/versions/${encodeURIComponent(versionId)}/download`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${requireAccessToken()}`,
        },
      },
    );
    if (!response.ok) {
      let detail = `Request failed (${response.status})`;
      try {
        const body = (await response.json()) as { detail?: unknown };
        if (typeof body.detail === "string") {
          detail = body.detail;
        }
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    const blob = await response.blob();
    const base =
      (opts?.suggestedBaseName || "contract").replace(/[^a-z0-9._-]+/gi, "-") || "contract";
    const name = filenameFromContentDisposition(
      response.headers.get("Content-Disposition"),
      `${base}.docx`,
    );
    triggerBrowserDownload(blob, name);
  } catch (err) {
    throw new Error(formatHostError(err));
  }
}

/** Last path segment of `Office.context.document.url` (decoded), or null if unavailable. */
function filenameFromOfficeDocumentUrl(): string | null {
  const raw = Office.context.document?.url;
  if (raw == null || raw === "") {
    return null;
  }
  const pathOnly = raw.split(/[?#]/)[0];
  const normalized = pathOnly.replace(/\\/g, "/");
  const lastSlash = normalized.lastIndexOf("/");
  const segment = lastSlash >= 0 ? normalized.slice(lastSlash + 1) : normalized;
  if (!segment) {
    return null;
  }
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Saved path name from Office, else document title + `.docx`, else `document.docx`. */
async function getDocumentFilenameForUpload(): Promise<string> {
  const fromUrl = filenameFromOfficeDocumentUrl();
  if (fromUrl) {
    return fromUrl;
  }
  try {
    return await Word.run(async (context) => {
      context.document.properties.load("title");
      await context.sync();
      const title = (context.document.properties.title ?? "").trim();
      if (title) {
        const safe = title.replace(/[/\\?%*:|"<>]/g, "-").trim();
        if (safe) {
          return /\.(docx?|doc)$/i.test(safe) ? safe : `${safe}.docx`;
        }
      }
      return "document.docx";
    });
  } catch {
    return "document.docx";
  }
}

/** Prefer `message` from JSON `{"message":"..."}`; otherwise `fallback`. */
async function parseUploadSuccessResponseMessage(response: Response, fallback: string): Promise<string> {
  const text = await response.text();
  const trimmed = text.trim();
  if (!trimmed) {
    return fallback;
  }
  try {
    const data = JSON.parse(trimmed) as unknown;
    if (data != null && typeof data === "object" && "message" in data) {
      const m = (data as { message?: unknown }).message;
      if (typeof m === "string" && m.trim() !== "") {
        return m;
      }
    }
  } catch {
    return trimmed.length > 0 ? trimmed : fallback;
  }
  return fallback;
}

/** Internal message so wrapped errors still match {@link isEmptyDocumentUploadError} (ES5 + `Error` subclass breaks `instanceof` in some hosts). */
const EMPTY_DOCUMENT_UPLOAD_MARKER = "CENNA_EMPTY_DOCUMENT_UPLOAD";

/** Raised when the API rejects the upload because the file is empty or has no body text. */
export class EmptyDocumentUploadError extends Error {
  constructor() {
    super(EMPTY_DOCUMENT_UPLOAD_MARKER);
    this.name = "EmptyDocumentUploadError";
    if (typeof Object.setPrototypeOf === "function") {
      Object.setPrototypeOf(this, EmptyDocumentUploadError.prototype);
    }
  }
}

/** Reliable in Office WebView where `instanceof` on `Error` subclasses can fail under ES5 emit. */
export function isEmptyDocumentUploadError(err: unknown): boolean {
  if (err instanceof EmptyDocumentUploadError) {
    return true;
  }
  if (typeof err !== "object" || err === null) {
    return false;
  }
  const e = err as Error;
  if (!(e instanceof Error)) {
    return false;
  }
  if (e.name === "EmptyDocumentUploadError") {
    return true;
  }
  if (e.message === EMPTY_DOCUMENT_UPLOAD_MARKER) {
    return true;
  }
  if (e.message === "EmptyDocumentUploadError") {
    return true;
  }
  return false;
}

function parseFastApiDetailFromBody(body: string): string | null {
  const t = body.trim();
  if (!t) {
    return null;
  }
  try {
    const j = JSON.parse(t) as { detail?: unknown };
    const d = j.detail;
    if (typeof d === "string") {
      return d;
    }
  } catch {
    return null;
  }
  return null;
}

function isEmptyDocumentUploadRejectedDetail(detail: string | null): boolean {
  if (detail == null) {
    return false;
  }
  const s = detail.trim();
  return s === "Empty document cannot be uploaded." || s === "empty_upload";
}

async function postCurrentDocumentToAgreementUploadPath(
  buildUploadUrl: (agreementVersionId: string) => string,
  onProgress: ProgressCallback | undefined,
  fallbackSuccessMessage: string
): Promise<string> {
  onProgress?.("Reading agreement version id…");
  const { agreementVersionId } = await getAgreementIdentifiersFromDocument();
  const uploadPath = buildUploadUrl(agreementVersionId);

  onProgress?.("Reading document…");
  const bytes = await readCompressedDocumentBytes();

  onProgress?.("Sending…");
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const form = new FormData();
  form.append("file", blob, await getDocumentFilenameForUpload());
  form.append("agreement_version_id", agreementVersionId);

  const response = await fetchApi(uploadPath, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${requireAccessToken()}`,
    },
    body: form,
  });

  if (!response.ok) {
    const detail = await response.text();
    const apiDetail = parseFastApiDetailFromBody(detail);
    if (response.status === 400 && isEmptyDocumentUploadRejectedDetail(apiDetail)) {
      throw new EmptyDocumentUploadError();
    }
    throw new Error(detail ? `Request failed (${response.status}): ${detail}` : `Request failed (${response.status})`);
  }

  return parseUploadSuccessResponseMessage(response, fallbackSuccessMessage);
}

export type SendIncomingSuccess = { outcome: "success"; message: string };

export type SendIncomingResult = "blocked_unsaved" | SendIncomingSuccess;

/** Shown when Send for review is blocked because the document is new or has unsaved edits. */
export const SEND_FOR_REVIEW_BLOCKED_UNSAVED_MESSAGE =
  "Save this document in Word before sending it for review.";

export async function sendCurrentDocumentToIncoming(onProgress?: ProgressCallback): Promise<SendIncomingResult> {
  try {
    const unsaved = await documentHasUnsavedChanges();
    if (unsaved) {
      return "blocked_unsaved";
    }
    const message = await postCurrentDocumentToAgreementUploadPath(
      agreementIncomingUploadPath,
      onProgress,
      "Document sent successfully."
    );
    return { outcome: "success", message };
  } catch (err) {
    if (isEmptyDocumentUploadError(err)) {
      throw err;
    }
    throw new Error(formatHostError(err));
  }
}

/** Returned when the user must resolve tracked changes in Word before uploading a clean copy. */
export const CLEAN_COPY_BLOCKED_TRACKED_CHANGES_MESSAGE =
  "We need the final document with no tracked changes. In Word, go to Review, accept or reject what's left (Accept All is fine when appropriate), save if prompted, then try Generate clean copy again.";

/** Returned when the document is new or has unsaved edits; user should save in Word first. */
export const CLEAN_COPY_BLOCKED_UNSAVED_MESSAGE =
  "We upload the saved file on your computer—unsaved edits are not included yet. Save in Word (Ctrl+S or Cmd+S, or File → Save), then try again.";

export type SendCleanCopySuccess = { outcome: "success"; message: string };

export type SendCleanCopyResult = "blocked_unsaved" | "blocked_tracked_changes" | SendCleanCopySuccess;

/** Same multipart pattern as {@link sendCurrentDocumentToIncoming}; targets the clean-document upload endpoint. */
export async function sendCurrentDocumentToCleanCopy(onProgress?: ProgressCallback): Promise<SendCleanCopyResult> {
  try {
    const gate = await preflightCleanCopyUpload();
    if (gate === "tracked") {
      return "blocked_tracked_changes";
    }
    if (gate === "unsaved") {
      return "blocked_unsaved";
    }
    const message = await postCurrentDocumentToAgreementUploadPath(
      agreementIncomingCleanUploadPath,
      onProgress,
      "Clean copy request completed."
    );
    return { outcome: "success", message };
  } catch (err) {
    if (isEmptyDocumentUploadError(err)) {
      throw err;
    }
    throw new Error(formatHostError(err));
  }
}

export type CompareAgreementOptions = {
  /**
   * When set, asks the backend to use that agreement file version as the compare baseline (e.g. previous version "1").
   * Sent with `agreement_version_id` / `agreement_id` in the JSON body.
   */
  compareWithVersion?: string;
};

export async function compareAgreementWithDocument(
  onProgress?: ProgressCallback,
  options?: CompareAgreementOptions
): Promise<void> {
  try {
    onProgress?.("Reading agreement ids from document…");
    const { agreementId, agreementVersionId } = await getAgreementIdentifiersFromDocument();

    onProgress?.("Recording authors on existing tracked changes…");
    const preCompareAuthors = await collectPreCompareTrackedChangeAuthors();
    const preCompareAuthorsLower = new Set(
      Array.from(preCompareAuthors)
        .map(normalizeAuthorKey)
        .filter((s) => s.length > 0)
    );

    onProgress?.("Saving your document for compare…");
    const userBytes = await readCompressedDocumentBytes();
    const userBase64 = arrayBufferToBase64(userBytes);

    onProgress?.("Fetching agreement for compare…");

    const body: Record<string, unknown> = {
      agreement_id: agreementId,
      agreement_version_id: agreementVersionId,
    };
    if (options?.compareWithVersion != null && String(options.compareWithVersion).trim() !== "") {
      body.compare_with_version = String(options.compareWithVersion).trim();
    }

    const response = await fetchApi(AGREEMENT_COMPARE_PATH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${requireAccessToken()}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const detail = await response.text();
      throw new Error(detail ? `Request failed (${response.status}): ${detail}` : `Request failed (${response.status})`);
    }

    const data = (await response.json()) as CompareResponse;
    const encoded = data.encoded;
    if (typeof encoded !== "string" || !encoded) {
      throw new Error("Response did not include a non-empty encoded string.");
    }

    const agreementBase64 = normalizeDocumentBase64(encoded);

    onProgress?.("Comparing (agreement baseline → your copy), then highlighting new differences…");

    await Word.run(async (context) => {
      context.document.insertFileFromBase64(agreementBase64, Word.InsertLocation.replace);
      context.document.compareFromBase64(userBase64, {
        compareTarget: Word.CompareTarget.compareTargetCurrent,
        ignoreAllComparisonWarnings: true,
        removeDateAndTime: false,
        removePersonalInformation: false,
        detectFormatChanges: true,
      });

      await context.sync();

      const trackedAfter = context.document.body.getTrackedChanges();
      trackedAfter.load("items");
      await context.sync();
      for (const tc of trackedAfter.items) {
        tc.load("author");
      }
      await context.sync();

      for (const tc of trackedAfter.items) {
        const auth = tc.author?.trim() ?? "";
        if (preCompareAuthorsLower.has(normalizeAuthorKey(auth))) {
          continue;
        }
        const range = tc.getRange();
        range.font.highlightColor = "Yellow";
      }

      await context.sync();

      try {
        const view = context.document.activeWindow.view;
        view.revisionsFilter.markup = Word.RevisionsMarkup.all;
        view.areFormatChangesDisplayed = true;
        view.areRevisionsAndCommentsDisplayed = true;
        await context.sync();
      } catch {
        // Compare + highlights already committed; user can set Review > Display for Markup manually.
      }
    });

    onProgress?.(
      "Comparison complete. Prior tracked-change authors unchanged; other differences use yellow highlight."
    );
  } catch (err) {
    let message = formatHostError(err);
    if (/compareFromBase64|insertFileFromBase64|not supported|ApiNotFound|InvalidArgument/i.test(message)) {
      message += " Compare requires Word on Windows or Mac (desktop), not Word on the web.";
    }
    throw new Error(message);
  }
}
